import { usePreviousRef } from '@sparemin/blockhead';
import { useEffect } from 'react';
import { ReadyState } from 'components/VideoPlayer/utils';
import { useEditSceneModalPlaybackContext } from './EditSceneModalPlaybackContext';
import { useEditSceneModalStateContext, useSlots } from './state';
import { drawLayout } from './utils';

export type UseDrawLayoutProps = {
  canvasRef: React.RefObject<HTMLCanvasElement>;
};

export default function useDrawLayout({ canvasRef }: UseDrawLayoutProps) {
  const { playerRef } = useEditSceneModalPlaybackContext();
  const {
    state: { isFrameSeeking },
  } = useEditSceneModalStateContext();

  const { slots: slotsToDraw } = useSlots();

  const slotsRef = usePreviousRef(slotsToDraw);

  useEffect(() => {
    const canvas = canvasRef.current;
    const videoEl = playerRef.current?.dangerousHTMLVideoElement;

    if (!canvas || !videoEl) {
      return undefined;
    }

    let animationFrameId: number;
    let isPlaying = false;

    function drawFrame() {
      const currentSlots = slotsRef.current;

      // optimization - only draw if we have slots and video is ready
      if (
        currentSlots.length > 0 &&
        videoEl.readyState >= ReadyState.HAVE_METADATA
      ) {
        drawLayout(currentSlots, canvas, videoEl);
      }

      // continue loop if plyaing (or if we need to keep checking)
      if (isPlaying) {
        animationFrameId = requestAnimationFrame(drawFrame);
      }
    }

    const handlePlay = () => {
      isPlaying = true;

      // don't cancel existing frame - just ensure loop continues
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(drawFrame);
      }
    };

    const handlePause = () => {
      isPlaying = false;

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = undefined;
      }
    };

    const handleSeeked = () => {
      if (isFrameSeeking) {
        return;
      }

      // force immediate redraw on seek, but don't restart loop
      drawFrame();
    };

    const handleLoadedMetadata = () => {
      // draw initial frame
      drawFrame();
    };

    videoEl.addEventListener('play', handlePlay);
    videoEl.addEventListener('pause', handlePause);
    videoEl.addEventListener('seeked', handleSeeked);
    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);

    if (videoEl.readyState >= HTMLMediaElement.HAVE_METADATA) {
      drawFrame();
      cancelAnimationFrame(animationFrameId);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      videoEl.removeEventListener('play', handlePlay);
      videoEl.removeEventListener('pause', handlePause);
      videoEl.removeEventListener('seeked', handleSeeked);
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [canvasRef, isFrameSeeking, playerRef, slotsRef]);

  // trigger drawLayout when slotsToDraw changes while the player is pausd (e.g. the
  // user changes the layout preset).  The effect above will handle drawing the
  // layout during playback
  useEffect(() => {
    const player = playerRef.current;

    if (
      player &&
      player.paused &&
      player.readyState >= ReadyState.HAVE_METADATA &&
      slotsToDraw.length > 0 &&
      canvasRef.current
    ) {
      drawLayout(
        slotsToDraw,
        canvasRef.current,
        player.dangerousHTMLVideoElement,
      );
    }
  }, [canvasRef, playerRef, slotsToDraw]);
}
